import {
  fmtInt,
  fmtMoney,
  fmtMoneyDetailed,
  fmtNum,
  fmtPct,
} from "../../../utils/format";
import { bySign, byPacingTarget, byVolumeBand, Tone } from "./columns";
import { EnrichedRow } from "./derive";

// ──────────────────────────────────────────────────────────────────────────────
// Column totals (table footer)
// ──────────────────────────────────────────────────────────────────────────────
// Kept out of columns.tsx — like columnMeta — so the column registry stays
// focused on rendering. Keyed by ColumnDef.id.
//
// Only columns whose roll-up actually means something get an entry here. Three
// rules decide that:
//   1. Money and same-unit counters add up → plain sum.
//   2. A percentage NEVER averages its per-row values (that would weight a
//      500 € line like a 50.000 € one) → ratio of the two aggregated parts.
//   3. Volume columns hold impressions, clicks, views or conversions depending
//      on each line's objective, so they only roll up while every visible row
//      shares the same objective. Mixed objectives → "—".
// Everything else (días, % tiempo, pacing % …) is deliberately left without a
// total because adding it up says nothing.

/** How a total is printed. `ratio` values are 0–1 — like the row field they
 *  aggregate — and get ×100 at render time; `percent` values already come ×100. */
export type TotalFormat =
  | "money"
  | "moneyDetailed"
  | "int"
  | "ratio"
  | "percent";

export interface ColumnTotal {
  /** Aggregate over the rows currently on screen (filters already applied).
   *  null → not meaningful for this set, the footer renders "—". */
  value: (rows: EnrichedRow[]) => number | null;
  format: TotalFormat;
  /** Decimals for the percentage formats (default 1). */
  decimals?: number;
  /** Shown in the footer tooltip so nobody has to guess how it rolled up. */
  hint: string;
  /** Conditional formatting, same thresholds as the body cells. */
  tone?: (v: number) => Tone | undefined;
}

type FieldPick = (r: EnrichedRow) => number | null | undefined;

/** Σ of a field, skipping null / non-finite values (e.g. the "—" of
 *  investmentNeededTotalReal). null when there was nothing to add at all. */
const sumBy =
  (pick: FieldPick) =>
  (rows: EnrichedRow[]): number | null => {
    let acc = 0;
    let counted = 0;
    rows.forEach((r) => {
      const v = pick(r);
      if (v != null && Number.isFinite(v)) {
        acc += v;
        counted += 1;
      }
    });
    return counted > 0 ? acc : null;
  };

/** Ratio of two sums — the correct roll-up for a percentage column. */
const ratioBy =
  (numerator: FieldPick, denominator: FieldPick) =>
  (rows: EnrichedRow[]): number | null => {
    const num = sumBy(numerator)(rows);
    const den = sumBy(denominator)(rows);
    if (num == null || den == null || den === 0) return null;
    return num / den;
  };

/** True while every row shown shares the same objective, i.e. while the volume
 *  columns are all counting the same thing. */
const sameObjective = (rows: EnrichedRow[]): boolean =>
  rows.length > 0 && rows.every((r) => r.objective === rows[0].objective);

/** Sum gated on a single objective — see rule 3 above. */
const volumeSumBy =
  (pick: FieldPick) =>
  (rows: EnrichedRow[]): number | null =>
    sameObjective(rows) ? sumBy(pick)(rows) : null;

/** Ratio gated on a single objective. */
const volumeRatioBy =
  (numerator: FieldPick, denominator: FieldPick) =>
  (rows: EnrichedRow[]): number | null =>
    sameObjective(rows) ? ratioBy(numerator, denominator)(rows) : null;

/** Weighted mean of a unit cost: Σ(unit cost × volume) / Σ volume.
 *  Gated on a single objective — a CPM and a CPC are not averageable. */
const unitCostBy =
  (unitCost: (r: EnrichedRow) => number, volume: (r: EnrichedRow) => number) =>
  (rows: EnrichedRow[]): number | null =>
    sameObjective(rows)
      ? ratioBy((r) => unitCost(r) * volume(r), volume)(rows)
      : null;

const SUM = "Suma de las filas visibles.";
const VOLUME_SUM =
  "Suma de las filas visibles. Solo se calcula si todas comparten objetivo (CPM, CPC…): volúmenes de distinta unidad no se suman.";
const VOLUME_RATIO =
  "Ratio sobre los volúmenes agregados. Solo se calcula si todas las filas comparten objetivo.";
const UNIT_COST =
  "Media ponderada por volumen. Solo se calcula si todas las filas comparten objetivo.";

export const COLUMN_TOTALS: Record<string, ColumnTotal> = {
  // ── Brief ─────────────────────────────────────────────────────────────────
  grossUnitCost: {
    value: unitCostBy(
      (r) => r.grossUnitCost,
      (r) => r.targetVolume
    ),
    format: "moneyDetailed",
    hint: `Coste unitario cliente medio. ${UNIT_COST}`,
  },
  netUnitCost: {
    value: unitCostBy(
      (r) => r.netUnitCost,
      (r) => r.targetVolume
    ),
    format: "moneyDetailed",
    hint: `Coste unitario plataforma medio. ${UNIT_COST}`,
  },
  totalInversion: {
    value: sumBy((r) => r.totalInversion),
    format: "money",
    hint: `Inversión cliente contratada. ${SUM}`,
  },
  margin: {
    value: ratioBy(
      (r) => r.margin * r.totalInversion,
      (r) => r.totalInversion
    ),
    format: "percent",
    hint: "Margen medio ponderado por inversión cliente, no la media simple de los márgenes.",
  },
  marginIdeal: {
    value: ratioBy(
      (r) => (r.marginIdeal == null ? null : r.marginIdeal * r.totalInversion),
      (r) => (r.marginIdeal == null ? null : r.totalInversion)
    ),
    format: "percent",
    hint: "Margen ideal medio ponderado por inversión cliente. Las filas sin coste real todavía no cuentan.",
  },
  platformInversion: {
    value: sumBy((r) => r.platformInversion),
    format: "money",
    hint: `Inversión plataforma planificada. ${SUM}`,
  },
  targetVolume: {
    value: volumeSumBy((r) => r.targetVolume),
    format: "int",
    hint: VOLUME_SUM,
  },

  // ── KPIs Generales ────────────────────────────────────────────────────────
  impressions: { value: sumBy((r) => r.impressions), format: "int", hint: SUM },
  clicks: { value: sumBy((r) => r.clicks), format: "int", hint: SUM },
  completeViews: {
    value: sumBy((r) => r.completeViews),
    format: "int",
    hint: SUM,
  },
  conversions: { value: sumBy((r) => r.conversions), format: "int", hint: SUM },

  // ── Alerta ────────────────────────────────────────────────────────────────
  expectedVolumePercent: {
    value: volumeRatioBy(
      (r) => r.expectedVolumeUntilYesterday,
      (r) => r.targetVolume
    ),
    format: "ratio",
    hint: `Σ volumen esperado / Σ volumen previsto. ${VOLUME_RATIO}`,
  },
  expectedVolumeUntilYesterday: {
    value: volumeSumBy((r) => r.expectedVolumeUntilYesterday),
    format: "int",
    hint: VOLUME_SUM,
  },
  obtainedVolumePercent: {
    value: volumeRatioBy(
      (r) => r.accumulatedVolumeUntilYesterday,
      (r) => r.targetVolume
    ),
    format: "ratio",
    tone: byVolumeBand,
    hint: `Σ volumen obtenido / Σ volumen previsto. ${VOLUME_RATIO}`,
  },
  accumulatedVolumeUntilYesterday: {
    value: volumeSumBy((r) => r.accumulatedVolumeUntilYesterday),
    format: "int",
    hint: VOLUME_SUM,
  },
  impressionsUntilYesterday: {
    value: sumBy((r) => r.impressions),
    format: "int",
    hint: SUM,
  },
  volumeVariation: {
    value: volumeSumBy((r) => r.volumeVariation),
    format: "int",
    tone: bySign,
    hint: `Volumen obtenido menos esperado, agregado. ${VOLUME_SUM}`,
  },

  // ── Pacing Inversión ──────────────────────────────────────────────────────
  totalPlatformCost: {
    value: sumBy((r) => r.totalPlatformCost),
    format: "money",
    hint: `Coste real gastado en plataforma. ${SUM}`,
  },
  pacingInvestmentPercent: {
    value: ratioBy(
      (r) => r.totalPlatformCost,
      (r) => r.platformInversion
    ),
    format: "ratio",
    tone: byPacingTarget,
    hint: "Σ coste total plataforma / Σ inversión plataforma, no la media de los pacings fila a fila.",
  },
  yesterdayPacingInvestmentVar: {
    value: ratioBy(
      (r) => r.yesterdayClientCost - r.yesterdayEstimatedClientCost,
      (r) => r.yesterdayEstimatedClientCost
    ),
    format: "ratio",
    tone: bySign,
    hint: "Σ coste cliente de ayer frente a Σ coste cliente estimado de ayer.",
  },
  investmentNeededTotalReal: {
    value: sumBy((r) => r.investmentNeededTotalReal),
    format: "money",
    hint: `${SUM} Las filas que aún no tienen coste real ("—") no entran.`,
  },
  investmentNeededRemainingReal: {
    value: sumBy((r) => r.investmentNeededRemainingReal),
    format: "money",
    hint: `${SUM} Las filas que aún no tienen coste real ("—") no entran.`,
  },
  realUnitCostPlatform: {
    value: unitCostBy(
      (r) => r.realUnitCostPlatform,
      (r) => r.accumulatedVolume
    ),
    format: "moneyDetailed",
    hint: UNIT_COST,
  },
  realUnitCostClient: {
    value: unitCostBy(
      (r) => r.realUnitCostClient,
      (r) => r.accumulatedVolume
    ),
    format: "moneyDetailed",
    hint: UNIT_COST,
  },

  // ── Otros ─────────────────────────────────────────────────────────────────
  addedCostsTotal: {
    value: sumBy((r) => r.addedCostsTotal),
    format: "money",
    hint: SUM,
  },
  accumulatedVolume: {
    value: volumeSumBy((r) => r.accumulatedVolume),
    format: "int",
    hint: VOLUME_SUM,
  },
  remainingVolume: {
    value: volumeSumBy((r) => r.remainingVolume),
    format: "int",
    hint: VOLUME_SUM,
  },
  dailyVolumeNeeded: {
    value: volumeSumBy((r) => r.dailyVolumeNeeded),
    format: "int",
    hint: VOLUME_SUM,
  },
  volumePercent: {
    value: volumeRatioBy(
      (r) => r.accumulatedVolume,
      (r) => r.targetVolume
    ),
    format: "ratio",
    tone: byVolumeBand,
    hint: `Σ volumen acumulado / Σ volumen previsto. ${VOLUME_RATIO}`,
  },
  volumeVariationPercent: {
    value: volumeRatioBy(
      (r) => r.volumeVariation,
      (r) => r.expectedVolumeUntilYesterday
    ),
    format: "ratio",
    tone: bySign,
    hint: `Σ variación / Σ volumen esperado. ${VOLUME_RATIO}`,
  },
  accumulatedInvestment: {
    value: sumBy((r) => r.accumulatedInvestment),
    format: "money",
    hint: SUM,
  },
  remainingInvestment: {
    value: sumBy((r) => r.remainingInvestment),
    format: "money",
    hint: SUM,
  },
  remainingPlatformInvestment: {
    value: sumBy((r) => r.remainingPlatformInvestment),
    format: "money",
    hint: SUM,
  },
  investmentNeededTotal: {
    value: sumBy((r) => r.investmentNeededTotal),
    format: "money",
    hint: SUM,
  },
  investmentNeededRemaining: {
    value: sumBy((r) => r.investmentNeededRemaining),
    format: "money",
    hint: SUM,
  },
  yesterdayImpressions: {
    value: sumBy((r) => r.yesterdayImpressions),
    format: "int",
    hint: SUM,
  },
  yesterdayClicks: {
    value: sumBy((r) => r.yesterdayClicks),
    format: "int",
    hint: SUM,
  },
  yesterdayCompleteViews: {
    value: sumBy((r) => r.yesterdayCompleteViews),
    format: "int",
    hint: SUM,
  },
  yesterdayConversions: {
    value: sumBy((r) => r.yesterdayConversions),
    format: "int",
    hint: SUM,
  },
  yesterdayRealCost: {
    value: sumBy((r) => r.yesterdayRealCost),
    format: "money",
    hint: SUM,
  },
  dailyTargetVolume: {
    value: volumeSumBy((r) => r.dailyTargetVolume),
    format: "int",
    hint: VOLUME_SUM,
  },
  yesterdayActualVolume: {
    value: volumeSumBy((r) => r.yesterdayActualVolume),
    format: "int",
    hint: VOLUME_SUM,
  },
  yesterdayVolumeDiff: {
    value: volumeSumBy((r) => r.yesterdayVolumeDiff),
    format: "int",
    tone: bySign,
    hint: VOLUME_SUM,
  },
  yesterdayVolumeVar: {
    value: volumeRatioBy(
      (r) => r.yesterdayVolumeDiff,
      (r) => r.dailyTargetVolume
    ),
    format: "ratio",
    tone: bySign,
    hint: `Σ diferencia de ayer / Σ objetivo diario. ${VOLUME_RATIO}`,
  },
  yesterdayClientCost: {
    value: sumBy((r) => r.yesterdayClientCost),
    format: "money",
    hint: SUM,
  },
  yesterdayEstimatedClientCost: {
    value: sumBy((r) => r.yesterdayEstimatedClientCost),
    format: "money",
    hint: SUM,
  },
  viewThroughRate: {
    value: ratioBy(
      (r) => r.completeViews,
      (r) => r.impressions
    ),
    format: "ratio",
    decimals: 2,
    hint: "Σ visualizaciones / Σ impresiones.",
  },
  clickThroughRate: {
    value: ratioBy(
      (r) => r.clicks,
      (r) => r.impressions
    ),
    format: "ratio",
    decimals: 2,
    hint: "Σ clicks / Σ impresiones.",
  },
  conversionRate: {
    value: ratioBy(
      (r) => r.conversions,
      (r) => r.clicks
    ),
    format: "ratio",
    decimals: 2,
    hint: "Σ conversiones / Σ clicks.",
  },

  // ── Entrega por formato (social, vídeo, display) ──────────────────────────
  engagements: {
    value: sumBy((r) => r.engagements),
    format: "int",
    hint: SUM,
  },
  engagementRate: {
    value: ratioBy(
      (r) => r.engagements,
      (r) => (r.engagements > 0 ? r.impressions : 0)
    ),
    format: "ratio",
    decimals: 2,
    hint: "Σ interacciones / Σ impresiones de las líneas que dan interacciones.",
  },
  measurableImpressions: {
    value: sumBy((r) => r.measurableImpressions),
    format: "int",
    hint: SUM,
  },
  viewableImpressions: {
    value: sumBy((r) => r.viewableImpressions),
    format: "int",
    hint: SUM,
  },
  viewabilityRate: {
    value: ratioBy(
      (r) => r.viewableImpressions,
      (r) => r.measurableImpressions
    ),
    format: "ratio",
    hint: "Σ impresiones visibles / Σ impresiones medibles.",
  },
  firstQuartileViews: {
    value: sumBy((r) => r.firstQuartileViews),
    format: "int",
    hint: SUM,
  },
  midpointViews: {
    value: sumBy((r) => r.midpointViews),
    format: "int",
    hint: SUM,
  },
  thirdQuartileViews: {
    value: sumBy((r) => r.thirdQuartileViews),
    format: "int",
    hint: SUM,
  },
  yesterdayEngagements: {
    value: sumBy((r) => r.yesterdayEngagements),
    format: "int",
    hint: SUM,
  },
  accumulatedClientCost: {
    value: sumBy((r) => r.accumulatedClientCost),
    format: "money",
    hint: SUM,
  },
  cpmReal: {
    value: ratioBy(
      (r) => r.accumulatedClientCost * 1000,
      (r) => r.impressions
    ),
    format: "moneyDetailed",
    hint: "Σ coste cliente × 1000 / Σ impresiones.",
  },
  cpcReal: {
    value: ratioBy(
      (r) => r.accumulatedClientCost,
      (r) => r.clicks
    ),
    format: "moneyDetailed",
    hint: "Σ coste cliente / Σ clicks.",
  },
  cpvReal: {
    value: ratioBy(
      (r) => r.accumulatedClientCost,
      (r) => r.completeViews
    ),
    format: "moneyDetailed",
    hint: "Σ coste cliente / Σ visualizaciones.",
  },
  cpeReal: {
    value: ratioBy(
      (r) => r.accumulatedClientCost,
      (r) => r.engagements
    ),
    format: "moneyDetailed",
    hint: "Σ coste cliente / Σ interacciones.",
  },
  cpaReal: {
    value: ratioBy(
      (r) => r.accumulatedClientCost,
      (r) => r.conversions
    ),
    format: "moneyDetailed",
    hint: "Σ coste cliente / Σ conversiones.",
  },
};

/** Total definition for a column id, or undefined when it has no meaningful one. */
export const getColumnTotal = (id: string): ColumnTotal | undefined =>
  COLUMN_TOTALS[id];

/** Footer text for an aggregated value. */
export function formatTotal(total: ColumnTotal, value: number): string {
  if (total.format === "money") return fmtMoney(value, 2);
  if (total.format === "moneyDetailed") return fmtMoneyDetailed(value);
  if (total.format === "int") return fmtInt(value);
  const scaled = total.format === "ratio" ? value * 100 : value;
  return fmtPct(scaled, total.decimals ?? 1);
}

/** Same value shaped for the CSV export — amounts stay numeric. */
export function totalCsvValue(
  total: ColumnTotal,
  value: number
): string | number {
  if (total.format === "money") return value;
  if (total.format === "moneyDetailed") return fmtNum(value, 4);
  if (total.format === "int") return Math.round(value);
  const scaled = total.format === "ratio" ? value * 100 : value;
  return fmtNum(scaled, total.decimals ?? 2);
}

/** Pre-computes every total once per row set so the footer and the CSV export
 *  can never disagree. */
export function computeTotals(rows: EnrichedRow[]): Map<string, number | null> {
  const map = new Map<string, number | null>();
  Object.entries(COLUMN_TOTALS).forEach(([id, total]) => {
    map.set(id, total.value(rows));
  });
  return map;
}
